/** @file PadletSpeechBubbleUtil */
// Original file: https://github.com/tldraw/tldraw/blob/579166a0e092a5ba49df40b684173e81f359751b/apps/examples/src/examples/speech-bubble/SpeechBubble/SpeechBubbleUtil.tsx
import { getFillDefForExport, getFontDefForExport } from '@@/bits/tldraw/PadletSpeechBubbleTool/defaultStyleDefs'
import {
  getSpeechBubbleVertices,
  getTailIntersectionPoint,
  PadletSpeechBubbleShapeBody,
} from '@@/bits/tldraw/PadletSpeechBubbleTool/PadletSpeechBubbleShapeBody'
import { SvgTextLabel } from '@@/bits/tldraw/PadletSpeechBubbleTool/SvgTextLabel'
import type { PadletSpeechBubbleShape, PadletSpeechBubbleShapeProps } from '@padlet/whiteboard-schema'
import { padletSpeechBubbleShapeProps } from '@padlet/whiteboard-schema'
import type { ReactElement } from 'react'
import type {
  Editor,
  Geometry2d,
  SvgExportContext,
  TLFontFace,
  TLHandleDragInfo,
  TLResizeInfo,
  TLShapeId,
} from 'tldraw'
import {
  Box,
  FONT_FAMILIES,
  getDefaultColorTheme,
  getFontsFromRichText,
  HTMLContainer,
  LABEL_FONT_SIZES,
  Polygon2d,
  renderHtmlFromRichTextForMeasurement,
  renderPlaintextFromRichText,
  RichTextLabel,
  ShapeUtil,
  STROKE_SIZES,
  SVGContainer,
  TEXT_PROPS,
  toRichText,
  useDefaultColorTheme,
  Vec,
} from 'tldraw'

// From https://github.com/tldraw/tldraw/blob/579166a0e092a5ba49df40b684173e81f359751b/packages/tldraw/src/lib/shapes/shared/default-shape-constants.ts#L57
export const LABEL_PADDING = 16
export const DEFAULT_SPEECH_BUBBLE_HEIGHT = 200
export const DEFAULT_SPEECH_BUBBLE_WIDTH = 300
export class PadletSpeechBubbleUtil extends ShapeUtil<PadletSpeechBubbleShape> {
  static override type = 'padlet-speech-bubble' as const
  static override props = padletSpeechBubbleShapeProps

  override isAspectRatioLocked(_shape: PadletSpeechBubbleShape): boolean {
    return false
  }

  override canResize(_shape: PadletSpeechBubbleShape): boolean {
    return true
  }

  override canEdit(): boolean {
    return true
  }

  getDefaultProps(): PadletSpeechBubbleShapeProps {
    return {
      w: DEFAULT_SPEECH_BUBBLE_WIDTH,
      h: DEFAULT_SPEECH_BUBBLE_HEIGHT,
      color: 'black',
      labelColor: 'black',
      size: 'm',
      font: 'draw',
      align: 'middle',
      fill: 'none',
      dash: 'draw',
      url: '',
      scale: 1,
      verticalAlign: 'start',
      growY: 0,
      richText: toRichText(''),
      tail: { x: 0.5, y: 1.5 },
    }
  }

  getHeight(shape: PadletSpeechBubbleShape): number {
    return shape.props.h + shape.props.growY
  }

  getGeometry(shape: PadletSpeechBubbleShape): Geometry2d {
    const speechBubbleGeometry = getSpeechBubbleVertices(shape)
    const body = new Polygon2d({
      points: speechBubbleGeometry,
      isFilled: true,
    })
    return body
  }

  override onHandleDrag(
    shape: PadletSpeechBubbleShape,
    { handle }: TLHandleDragInfo<PadletSpeechBubbleShape>,
  ): { id: TLShapeId; type: 'padlet-speech-bubble'; props: Partial<PadletSpeechBubbleShapeProps> } {
    return {
      ...shape,
      props: {
        tail: {
          x: handle.x / shape.props.w,
          y: handle.y / this.getHeight(shape),
        },
      },
    }
  }

  override onBeforeCreate(next: PadletSpeechBubbleShape): PadletSpeechBubbleShape {
    return this.getGrowY(next, next.props.growY)
  }

  override onBeforeUpdate(prev: PadletSpeechBubbleShape, shape: PadletSpeechBubbleShape): PadletSpeechBubbleShape {
    const { w, tail } = shape.props
    const fullHeight = this.getHeight(shape)

    const { segmentsIntersection, insideShape } = getTailIntersectionPoint(shape)

    const slantedLength = Math.hypot(w, fullHeight)
    const MIN_DISTANCE = slantedLength / 5
    const MAX_DISTANCE = slantedLength / 1.5

    const tailInShapeSpace = new Vec(tail.x * w, tail.y * fullHeight)

    const distanceToIntersection = tailInShapeSpace.dist(segmentsIntersection)
    const center = new Vec(w / 2, fullHeight / 2)
    const tailDirection = Vec.Sub(tailInShapeSpace, center).uni()

    let newPoint = tailInShapeSpace

    if (insideShape) {
      newPoint = Vec.Add(segmentsIntersection, tailDirection.mul(MIN_DISTANCE))
    } else {
      if (distanceToIntersection <= MIN_DISTANCE) {
        newPoint = Vec.Add(segmentsIntersection, tailDirection.mul(MIN_DISTANCE))
      } else if (distanceToIntersection >= MAX_DISTANCE) {
        newPoint = Vec.Add(segmentsIntersection, tailDirection.mul(MAX_DISTANCE))
      }
    }

    const next = {
      ...shape,
      props: {
        ...shape.props,
        tail: {
          x: newPoint.x / w,
          y: newPoint.y / fullHeight,
        },
      },
    }

    return this.getGrowY(next, prev.props.growY)
  }

  // From https://github.com/tldraw/tldraw/blob/8bbfe91e8805198827a48c5410d7e741f416ac1d/packages/tldraw/src/lib/shapes/geo/GeoShapeUtil.tsx#L548
  override toSvg(shape: PadletSpeechBubbleShape, ctx: SvgExportContext): ReactElement {
    // We need to scale the shape to 1x for export
    const newShape = {
      ...shape,
      props: {
        ...shape.props,
        w: shape.props.w / shape.props.scale,
        h: shape.props.h / shape.props.scale,
      },
    }
    const props = newShape.props
    ctx.addExportDef(getFillDefForExport(props.fill))
    const theme = getDefaultColorTheme(ctx)

    let textEl
    const plaintext = renderPlaintextFromRichText(this.editor, props.richText)
    if (plaintext !== '') {
      ctx.addExportDef(getFontDefForExport(props.font))

      const bounds = new Box(0, 0, props.w, props.h + props.growY)
      textEl = (
        <SvgTextLabel
          fontSize={LABEL_FONT_SIZES[props.size]}
          font={props.font}
          align={props.align}
          verticalAlign={props.verticalAlign}
          text={plaintext}
          labelColor={theme[props.labelColor].solid}
          bounds={bounds}
          padding={16}
        />
      )
    }

    return (
      <>
        <PadletSpeechBubbleShapeBody shape={shape} />
        {textEl}
      </>
    )
  }

  override getText(shape: PadletSpeechBubbleShape): string {
    return renderPlaintextFromRichText(this.editor, shape.props.richText)
  }

  override getFontFaces(shape: PadletSpeechBubbleShape): TLFontFace[] {
    return getFontsFromRichText(this.editor, shape.props.richText, {
      family: `tldraw_${shape.props.font}`,
      weight: 'normal',
      style: 'normal',
    })
  }

  component(shape: PadletSpeechBubbleShape): ReactElement {
    const {
      id,
      type,
      props: { font, size, align, richText, fill },
    } = shape
    const isSelected = shape.id === this.editor.getOnlySelectedShapeId()
    const theme = useDefaultColorTheme()
    const isEditingAnything = this.editor.getEditingShapeId() !== null
    const plaintext = renderPlaintextFromRichText(this.editor, richText)
    const showHtmlContainer = isEditingAnything || plaintext !== ''

    return (
      <>
        <SVGContainer id={shape.id}>
          <PadletSpeechBubbleShapeBody shape={shape} />
        </SVGContainer>
        {showHtmlContainer && (
          <HTMLContainer
            style={{
              overflow: 'hidden',
              width: shape.props.w,
              height: shape.props.h + shape.props.growY,
            }}
          >
            <RichTextLabel
              shapeId={id}
              type={type}
              font={font}
              fontSize={LABEL_FONT_SIZES[size] * shape.props.scale}
              lineHeight={TEXT_PROPS.lineHeight}
              padding={LABEL_PADDING * shape.props.scale}
              fill={fill}
              align={align}
              verticalAlign={shape.props.verticalAlign}
              richText={richText}
              isSelected={isSelected}
              labelColor={theme[shape.props.labelColor].solid}
              wrap
            />
          </HTMLContainer>
        )}
      </>
    )
  }

  indicator(shape: PadletSpeechBubbleShape): ReactElement {
    const vertices = getSpeechBubbleVertices(shape)
    const start = `${vertices[0].x},${vertices[0].y}`
    const points = vertices
      .slice(1)
      .map((v) => `${v.x},${v.y}`)
      .join(' L ')
    const pathData = `M ${start} L ${points} Z`
    return <path d={pathData} />
  }

  // From https://github.com/tldraw/tldraw/blob/8bbfe91e8805198827a48c5410d7e741f416ac1d/packages/tldraw/src/lib/shapes/geo/GeoShapeUtil.tsx#L593
  override onResize(
    shape: PadletSpeechBubbleShape,
    { handle, newPoint, scaleX, scaleY, initialShape }: TLResizeInfo<PadletSpeechBubbleShape>,
  ): {
    x: number
    y: number
    props: {
      w: number
      h: number
      growY: number
    }
  } {
    const unscaledInitialW = initialShape.props.w / initialShape.props.scale
    const unscaledInitialH = initialShape.props.h / initialShape.props.scale
    const unscaledGrowY = initialShape.props.growY / initialShape.props.scale
    // use the w/h from props here instead of the initialBounds here,
    // since cloud shapes calculated bounds can differ from the props w/h.
    let unscaledW = unscaledInitialW * scaleX
    let unscaledH = (unscaledInitialH + unscaledGrowY) * scaleY
    let overShrinkX = 0
    let overShrinkY = 0
    const MIN_SIZE_WITH_LABEL = 17 * 3
    const min = MIN_SIZE_WITH_LABEL

    if (renderPlaintextFromRichText(this.editor, shape.props.richText).trim() !== '') {
      let newW = Math.max(Math.abs(unscaledW), min)
      let newH = Math.max(Math.abs(unscaledH), min)

      if (newW < min && newH === min) newW = min
      if (newW === min && newH < min) newH = min

      const unscaledLabelSize = getUnscaledLabelSize(this.editor, {
        ...shape,
        props: {
          ...shape.props,
          w: newW * shape.props.scale,
          h: newH * shape.props.scale,
        },
      })

      const nextW = Math.max(Math.abs(unscaledW), unscaledLabelSize.w) * Math.sign(unscaledW)
      const nextH = Math.max(Math.abs(unscaledH), unscaledLabelSize.h) * Math.sign(unscaledH)
      overShrinkX = Math.abs(nextW) - Math.abs(unscaledW)
      overShrinkY = Math.abs(nextH) - Math.abs(unscaledH)

      unscaledW = nextW
      unscaledH = nextH
    }

    const scaledW = unscaledW * shape.props.scale
    const scaledH = unscaledH * shape.props.scale

    const offset = new Vec(0, 0)

    // x offsets

    if (scaleX < 0) {
      offset.x += scaledW
    }

    if (handle === 'left' || handle === 'top_left' || handle === 'bottom_left') {
      offset.x += scaleX < 0 ? overShrinkX : -overShrinkX
    }

    // y offsets

    if (scaleY < 0) {
      offset.y += scaledH
    }

    if (handle === 'top' || handle === 'top_left' || handle === 'top_right') {
      offset.y += scaleY < 0 ? overShrinkY : -overShrinkY
    }

    const { x, y } = offset.rot(shape.rotation).add(newPoint)

    return {
      x,
      y,
      props: {
        w: Math.max(Math.abs(scaledW), 1),
        h: Math.max(Math.abs(scaledH), 1),
        growY: 0,
      },
    }
  }

  getGrowY(shape: PadletSpeechBubbleShape, prevGrowY = 0): PadletSpeechBubbleShape {
    const PADDING = 17

    const nextTextSize = this.editor.textMeasure.measureText(
      renderPlaintextFromRichText(this.editor, shape.props.richText),
      {
        ...TEXT_PROPS,
        fontFamily: FONT_FAMILIES[shape.props.font],
        fontSize: LABEL_FONT_SIZES[shape.props.size],
        maxWidth: shape.props.w - PADDING * 2,
      },
    )

    const nextHeight = nextTextSize.h + PADDING * 2

    let growY = 0

    if (nextHeight > shape.props.h) {
      growY = nextHeight - shape.props.h
    } else {
      if (prevGrowY !== 0) {
        growY = 0
      }
    }

    return {
      ...shape,
      props: {
        ...shape.props,
        growY,
      },
    }
  }
}

// Copied from https://github.com/tldraw/tldraw/blob/8bbfe91e8805198827a48c5410d7e741f416ac1d/packages/tldraw/src/lib/shapes/geo/GeoShapeUtil.tsx#L851
function getUnscaledLabelSize(editor: Editor, shape: PadletSpeechBubbleShape): { w: number; h: number } {
  const { richText, font, size, w } = shape.props

  if (richText == null || renderPlaintextFromRichText(editor, richText) === '') {
    return { w: 0, h: 0 }
  }

  const minSize = editor.textMeasure.measureText('w', {
    ...TEXT_PROPS,
    fontFamily: FONT_FAMILIES[font],
    fontSize: LABEL_FONT_SIZES[size],
    maxWidth: 100,
  })

  const html = renderHtmlFromRichTextForMeasurement(editor, richText)
  const textSize = editor.textMeasure.measureText(html, {
    ...TEXT_PROPS,
    fontFamily: FONT_FAMILIES[font],
    fontSize: LABEL_FONT_SIZES[size],
    minWidth: minSize.w,
    maxWidth: Math.max(
      // Guard because a DOM nodes can't be less 0
      0,
      // A 'w' width that we're setting as the min-width
      Math.ceil(minSize.w + STROKE_SIZES[size]),
      // The actual text size
      Math.ceil(w / shape.props.scale - LABEL_PADDING * 2),
    ),
  })

  return {
    w: textSize.w + LABEL_PADDING * 2,
    h: textSize.h + LABEL_PADDING * 2,
  }
}
